import { Component, Input, OnInit, OnDestroy, AfterViewInit, ViewChild, ElementRef } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UserService } from '../../services/user.service';
import { ConfigService } from '../../services/config.service';
import { AlertsService } from '../../services/alerts.service';
import { ProductService } from '../../services/product.service';
import { MapService } from '../../map/services/map.service';
import { ImportLocationsComponent } from '../import-locations/import-locations.component';

import { BaseMap, MapControl, LengthUnit } from '../../models/map.interface';

import { loadModules } from 'esri-loader';

declare let Terraformer: any; // terraformer-wkt-parser.js, https://github.com/esri/terraformer-wkt-parser

/** 
 * Component allowing addition of location data (latitude, longitude) to a product.
 */
@Component({
	selector: 'geo-tag',
	templateUrl: './geo-tag.component.html',
	styleUrl: './geo-tag.component.css'
})
export class GeoTagComponent implements OnInit, OnDestroy, AfterViewInit {
	@Input() productGroup: any;

	@ViewChild('mapContainer') mapContainer!: ElementRef;

	LOCALIZATION: any;

	mapId: string = 'geotag-map';
	mapView: any;
	mapLayer: any;
	basemapConfigs: BaseMap[] = [];
	searchWidget: any;
	sketchViewModel: any;

	mapControl!: MapControl;
	lengthUnits: LengthUnit[] = [];
	tools = {
		activeShape: ''
	};
	search = {
		x: "",
		y: ""
	};
	mode: string = 'map'; // map, entity, location
	selected: any = {};

	extractOpts = {
		isPlaces: false,
		isCoordinates: false,
		isCountries: false,
	};
	isEntitiesExtraction: boolean = false;
	selectedEntities: any[] = [];
	geotagEntities: any[] = [];

	mylocations: any[] = [];
	constraints: any = {};
	pagination = {
		page: 1,
		elementsPerPage: 10,
		sorting: {
			sort: 'name,desc',
		},
		totalElements: 0,
		pageChanged: (newPage: any) => {
			this.pagination.page = newPage;
			this.loadSavedLocations(this.pagination.page);
		}
	};

	inprogress: boolean = false;

	constructor(
		private userService: UserService,
		private configService: ConfigService,
		private alertsService: AlertsService,
		private productService: ProductService,
		private mapService: MapService,
		private ngbmodalService: NgbActiveModal,
		private ngbModal: NgbModal
	) { }

	ngOnInit(): void {
		this.LOCALIZATION = this.userService.LOCALIZATION;

		this.basemapConfigs = this.configService.getConfig()?.esri?.basemaps || [];
		this.mapControl = this.mapService.getMapControl();
		this.lengthUnits = this.mapService.getLengthUnits();

		this.userService.isFacadeEnabled('OpensextantFacade').then(
			(available: boolean) => {
				this.isEntitiesExtraction = available;
			}
		);

		this.loadSavedLocations();
	}

	ngAfterViewInit(): void {
		this.initializeMap();
	}

	ngOnDestroy() {
		if (this.mapView) {
			this.mapView.destroy();
		}
	}

	private async initializeMap(): Promise<any> {
		this.mapView = await this.loadMap();
		this.mapService.loadBaseMap(this.mapView);

		this.mapView.when(() => {
			console.log("%s view is ready. All layers loaded.", this.mapId);

			let coords = this.configService.getConfig().esri.center ? this.configService.getConfig().esri.center.split(",") : ["0", "0"];
			this.mapService.centerAndZoomMap(this.mapView, coords, this.configService.getConfig().esri.zoom);

			this.onMapLoaded();
		});
	}

	private async loadMap(): Promise<any> {
		try {
			const [Map, MapView] = await loadModules(['esri/Map', 'esri/views/MapView']);
			const map = new Map({
				basemap: 'topo-vector'
			});

			const view = new MapView({
				container: this.mapContainer.nativeElement,
				map: map,
				center: [-118, 34],
				zoom: 10
			});

			return view;
		} catch (err) {
			console.error("ArcGIS API load error: ", err)
			return undefined;
		}
	}

	async getMapLayer(id: string): Promise<any> {
		const [GraphicsLayer] = await loadModules(['esri/layers/GraphicsLayer']);

		let layer = this.mapView.map.findLayerById(id);
		if (layer) return layer;

		switch (id) {
			case "geotag":
				layer = new GraphicsLayer({
					id: "geotag"
				});
				break;
		}

		if (layer) {
			this.mapView.map.add(layer);
			console.log("Created new layer with id '%s'", layer.id);
		}
		return layer;
	}

	activeShape(shape: string): void {
		this.sketchViewModel.cancel();
		this.mapView.navigationConstraints = {
			allowPan: true,
			allowZoom: true
		};
		if (shape === this.tools.activeShape) {
			this.tools.activeShape = '';
		} else {
			this.tools.activeShape = shape;
			this.activateDrawTool(shape);
		}
	}

	activateDrawTool(tool: string): void {
		this.mapView.navigationConstraints = {
			allowPan: false,
			allowZoom: false
		};
		this.sketchViewModel.create(tool.toLowerCase());
	}

	private async onMapLoaded(): Promise<any> {
		const [Search, SketchViewModel, Graphic] =
			await loadModules([
				'esri/widgets/Search',
				'esri/widgets/Sketch/SketchViewModel',
				'esri/Graphic'
			]);

		this.searchWidget = new Search({
			view: this.mapView,
			container: "geotagLocationBar"
		});
		this.searchWidget.on("search-complete", (event: any) => {
			// this.searchWidgetResults = event.results[0].results;
			console.log(event.results[0].results);
		});
		this.searchWidget.on("select-result", (event: any) => {
			let geojson = Terraformer.ArcGIS.parse(event.result.feature.geometry);
			let wkt = Terraformer.WKT.convert(geojson);
			this.selected = { 'geo': wkt };

			// Build a custom feature display. Can probably replace this with a template.
			let content =
				`<div class="map-searchwidget-content">` +
				`<div class="searchwidget-address">${event.result.feature.attributes.Match_addr}</div>` +
				`<div class="searchwidget-address-type">${event.result.feature.attributes.Addr_type} in ${event.result.feature.attributes.City}</div>` +
				`</div>`;

			this.mapView.goTo({
				target: event.result.feature.geometry,
				zoom: 15
			}).then(() => {
				// The popup displays docked by default in narrow viewports
				this.mapView.popup.open({
					title: event.result.name,
					content: content,
					location: event.result.feature.geometry,
					shouldFocus: true
				});
			});

			// Add a location point graphic to the map
			let graphic = new Graphic({
				geometry: event.result.feature.geometry,
				symbol: this.mapService.getSymbol('locate-point'),
				attributes: {
					"wkt": wkt
				}
			});

			this.mapLayer.add(graphic);
		});


		this.mapLayer = await this.getMapLayer("geotag");

		this.sketchViewModel = new SketchViewModel({
			view: this.mapView,
			layer: this.mapLayer,
			showTooltips: false,
			pointSymbol: {
				type: "simple-marker",
				style: "square",
				color: "red",
				size: "8px"
			},
			polylineSymbol: {
				type: "simple-line",
				color: "green",
				width: "2px"
			},
			polygonSymbol: {
				type: "simple-fill",
				color: [255, 255, 255, 0.5],
				outline: {
					color: "blue",
					width: 1
				}
			}
		});
		this.sketchViewModel.on("create", (event: any) => {
			if (event.state === "complete") {
				// Handle the completion of the drawing
				console.log("Drawing completed:", event.graphic);
				this.addGraphic(event.graphic, event);
			}
		});
	}

	/**
	 * Generates a WKT string for geo-search using the WFS service.
	 */
	private async addGraphic(graphic: any, event: any): Promise<any> {
		const [Graphic, Point, SpatialReference, geometryEngine, webMercatorUtils, normalizeUtils, BufferParameters, geometryService] =
			await loadModules([
				'esri/Graphic',
				'esri/geometry/Point',
				'esri/geometry/SpatialReference',
				'esri/geometry/geometryEngine',
				'esri/geometry/support/webMercatorUtils',
				'esri/geometry/support/normalizeUtils',
				'esri/rest/support/BufferParameters',
				'esri/rest/geometryService'
			]);

		this.mapLayer.removeAll();

		let wktString = null;

		if (event.tool === "circle" || graphic.geometry._srsShape === "circle") {
			// Find the WKT string:
			// Extract the Circle Geometry - Get the geometry of the graphic created by the SketchViewModel
			// Calculate the Center Point and Radius
			// Radius Calculation: For a polygon, the radius is calculated by measuring the distance from the center to a point on the perimeter (e.g., the first point in the polygon's ring)
			// Construct the WKT String - Format the WKT string for the circle using the center point coordinates and the radius

			// Coordinate systems:
			// Mercator (WGS 84 / Pseudo-Mercator)
			//    The most commonly used projection in web mapping (including ArcGIS Online) is Web Mercator. The default units are in meters.
			// Geographic Coordinate System (WGS 84)
			//    If you're working with geographic coordinates (latitude and longitude), the default units are in degrees.
			//  Projected Coordinate Systems
			//    For other projected coordinate systems (like UTM or State Plane), the default units can vary.	

			// Get the center point (Web Mercator)
			let center = graphic.geometry.centroid;
			let centerPoint = center;

			// Get the first point in the geometry ring (Web Mercator) 
			// Assumes the geometry is a Polygon
			let firstPointCoords = graphic.geometry.rings[0][0];
			let firstPoint = new Point({
				x: firstPointCoords[0],
				y: firstPointCoords[1],
				spatialReference: { wkid: 102100 }
			});

			// Get the radius (using Web Mercator units)
			// Calculate the distance in the given lengthUnit (eg: meters) from center to some point on the perimeter
			let radius = geometryEngine.distance(center, firstPoint, this.mapControl.lengthUnit);

			// Get the unit of conversion for this.mapControl.lengthUnit, to xform into meters 
			//			let conversionUnit = $.grep(this.lengthUnits, function(n, i) {
			//				if (n.engineUnit == this.mapControl.lengthUnit) {
			//					return n.name;
			//				};
			//			})[0];
			let conversionUnit = this.lengthUnits.find((unit: any) => unit.engineUnit === this.mapControl.lengthUnit);

			// Get the radius in meters
			let radiusInMeters = radius / (conversionUnit?.toMeter || 1);

			// Convert the center to geographic coordinates for the WKT string
			let centerGeographic = webMercatorUtils.webMercatorToGeographic(center);

			// Create WKT string SPECIFICALLY FOR services.buildQuery: 
			// CIRCLE(center.y, center.x d=radiusInMeters)
			wktString = "CIRCLE(" + centerGeographic.y.toFixed(3) + "," + centerGeographic.x.toFixed(3) + " d=" + radiusInMeters + ")";

			console.log("Generated WKT AOI search string for length unit: %s", this.mapControl.lengthUnit);
			console.log(wktString);

		} else if (event.tool === "point") {
			let geojson = Terraformer.ArcGIS.parse(graphic.geometry);
			wktString = Terraformer.WKT.convert(geojson);

		} else if (graphic.geometry.type === 'line' || graphic.geometry.type === 'polyline') {
			let drawBuffer = (bufferedGeometries: any[]) => {
				let geom = bufferedGeometries[0];
				let geojson = Terraformer.ArcGIS.parse(geom);
				let newWktString = Terraformer.WKT.convert(geojson);

				// Add the graphic to the map
				let newGraphic = new Graphic({
					geometry: graphic.geometry,
					symbol: (graphic.geometry.type == "point") ? this.mapService.getSymbol('locate-point') : this.mapService.getSymbol('aoi'),
				});
				this.mapLayer.add(newGraphic);

				this.sketchViewModel.cancel();
				this.mapView.navigationConstraints = {
					allowPan: true,
					allowZoom: true
				};
				this.tools.activeShape = '';

				let taxLotExtent = geom.extent;
				this.mapView.goTo(taxLotExtent, { animate: true });
			};

			let params = new BufferParameters();
			params.distances = [event.buffer.distance];
			params.outSpatialReference = new SpatialReference({ wkid: 102100 });
			params.inSpatialReference = new SpatialReference({ wkid: 102100 });
			params.unit = geometryService[event.buffer.unit];
			params.unionResults = false;
			params.geodesic = false;

			const functdrawBuffer = (bufferedGeometries: any) => {
				const geom = bufferedGeometries[0];
				this.mapLayer.add(geom);
			};

			normalizeUtils.normalizeCentralMeridian([graphic.geometry]).then(
				(normalizedGeometries: any) => {
					let normalizedGeometry = normalizedGeometries[0];
					if (normalizedGeometry.type === "polygon") {
						// If geometry is a polygon then simplify polygon.  This will make the user drawn polygon topologically correct.
						geometryService.simplify(this.configService.getConfig().esri.geometryService, [normalizedGeometry]).then(
							(geometries: any) => {
								params.geometries = geometries;
								geometryService.buffer(params, functdrawBuffer);
							},
							(err: any) => {
								console.error("Error simplifying geometry: ", err);
							}
						);
					} else {
						params.geometries = [normalizedGeometry];
						geometryService.buffer(params, drawBuffer);
					}
				}
			);

		} else {
			// Catchall --> polygon
			let geom;
			if (graphic.geometry.spatialReference && graphic.geometry.spatialReference.wkid === 4326) {
				geom = graphic.geometry;
			} else {
				geom = webMercatorUtils.webMercatorToGeographic(graphic.geometry);
			}

			for (let i = 0; i < geom.rings.length; i++) {
				let ring = geom.rings[i];
				for (let p = 0; p < ring.length; p++) {
					let point = ring[p];
					point[0] = point[0];
					point[1] = point[1];
				}
			}

			let geojson = Terraformer.ArcGIS.parse(geom);
			wktString = Terraformer.WKT.convert(geojson);
		}

		// Add the graphic to the map
		let newGraphic = new Graphic({
			geometry: graphic.geometry,
			symbol: (graphic.geometry.type === "point") ? this.mapService.getSymbol('locate-point') : this.mapService.getSymbol('aoi'),
			attributes: {
				"wkt": wktString
			}
		});
		this.mapLayer.add(newGraphic);

		this.sketchViewModel.cancel();
		this.mapView.navigationConstraints = {
			allowPan: true,
			allowZoom: true
		};
		this.tools.activeShape = '';

		this.selected = { 'geo': wktString };
		console.log("selected: %s", this.selected);
	}

	changeBasemap(basemapName: string): void {
		this.mapService.loadBaseMap(this.mapView, basemapName);
	}

	clearMap(): void {
		this.selected = null;
		this.mapLayer.removeAll();
		this.sketchViewModel.cancel();
		this.tools.activeShape = '';
		this.mapView.closePopup();
	}

	zoomTo(): void {
		this.mapService.centerAndZoomMap(this.mapView, [this.search.x, this.search.y], 13);
	}

	fullExtent(): void {
		let coords = this.configService.getConfig().esri.center ? this.configService.getConfig().esri.center.split(",") : ["0", "0"];
		this.mapService.centerAndZoomMap(this.mapView, coords, this.configService.getConfig().esri.zoom);
	}

	selectEntity(entity: any): void {
		this.selected = null;

		const existsByOffset = this.selectedEntities.find((e: any) =>
			e.offset === entity.offset
		);
		if (existsByOffset?.length == 0) {
			this.selectedEntities.push(entity);
		} else {
			for (let i = 0; i < this.selectedEntities.length; i++) {
				if (this.selectedEntities[i].offset === entity.offset) {
					this.selectedEntities.splice(i, 1);
					break;
				}
			}
		}
	}

	isSelectedEntity(entity: any): boolean {
		const existsByOffset = this.selectedEntities.find((e: any) =>
			e.offset === entity.offset
		);

		return existsByOffset?.length == 0;
	}
	
	private getProductIds(): any[] {
		if (this.productGroup.recordId) {
			return [this.productGroup.recordId];
		}
	
		let prodIds = this.productGroup.records.map((record: any) => {
			return record.id
		});
		return prodIds;
	}

	extractTag(): void {
		this.inprogress = true;

		let payload = {
			isPlaces: this.extractOpts.isPlaces,
			isCoordinates: this.extractOpts.isCoordinates,
			isCountries: this.extractOpts.isCountries,
			ids: this.getProductIds()
		};

		this.productService.extractEntities(payload).then(
			(response: any) => {
				this.inprogress = false;
				this.geotagEntities = response.annotations;
			},
			(err: any) => {
				console.error("Failed to extract geo entities: ", err);
				this.inprogress = false;
				this.alertsService.error({
					title: "Extraction Error",
					message: "Failed to extract geographic entities.",
					delay: 3000
				});
			}
		);
	}

	private updateProductMetadata(): void {
		this.inprogress = true;

		let metadata: { [key: string]: any } = {};
		if (this.selectedEntities.length > 0) {
			metadata["geo"] = [];
			for (let x of this.selectedEntities) {
				if (x.lon && x.lat) {
					metadata["geo"].push('POINT(' + x.lon + ' ' + x.lat + ')');
				}
			}
		} else {
			metadata["geo"] = [this.selected.geo];
		}

		let payload = {
			replaceAll: false,
			replaceFieldValues: true,
			metadata: metadata,
			ids: this.getProductIds()
		};

		this.productService.massUpdateMetadata(payload).then(
			(response: any) => {
				this.inprogress = false;
				this.alertsService.success({
					title: "Update Success",
					message: "Geometry has been updated for the selected files. Please wait for the index to update.",
					delay: 5000
				});
				this.close();
			},
			(err: any) => {
				console.error("Failed to update product metadata: ", err);
				this.inprogress = false;
				this.alertsService.error({
					title: "Update Failed",
					message: "Failed to update product geometry.",
					delay: 3000
				});
			}
		);
	}

	private returnData(): void {
		if (this.selectedEntities.length > 0) {
			let geoPoints = [];
			for (let x of this.selectedEntities) {
				if (x.lon && x.lat) {
					geoPoints.push('POINT(' + x.lon + ' ' + x.lat + ')');
				}
			}
			this.proceed(geoPoints);
		} else {
			this.proceed(this.selected.geo);
		}
	}

	tag(): void {
		if (this.productGroup.commit) {
			if (this.selected == null && this.selectedEntities.length == 0) {
				return;
			}
			this.updateProductMetadata();
		}
		else {
			this.returnData();
		}
	}

	/**
	 * Loads the user's saved AOI locations using the current constraints.
	 */
	loadSavedLocations(page?: number): void {
		this.inprogress = true;

		if (page) {
			this.pagination.page = page;
		}

		let index = this.pagination.page - 1;

		this.mapService.getSavedLocations(index, this.pagination, this.constraints).then(
			(data: any) => {
				if (!data) {
					this.mylocations = [];
				} else {
					this.mylocations = data.content;
					this.pagination.page = data.page.number + 1;
					this.pagination.elementsPerPage = data.page.size;
					this.pagination.totalElements = data.page.totalElements;
				}
				this.inprogress = false;
			},
			(err: any) => {
				console.error("Failed to fetch user's saved AOI locations: ", err);
				this.mylocations = [];
				this.inprogress = false;
			}
		);
	}

	/**
	 * Imports AOI locations into the user's saved set.
	 */
	importLocations(): void {
		const modalRef = this.ngbModal.open(ImportLocationsComponent, {});

		modalRef.result.then(
			(payload: any) => {
				if (payload?.result.answer === 'ok') {
					this.loadSavedLocations(1);
				}
			},
			(reason: any) => {
				console.log('Modal dismissed');
			}
		);
	}

	selectLocation(location: any): void {
		this.selected = location;
	}

	/**
	 * Closes the modal, returning a result of ok and payload.
	 */
	proceed(data: any): void {
		let caller = {
			callerName: 'geoTag',
			result: data
		};

		this.ngbmodalService.close({
			result: { answer: 'ok', caller: caller },
			timestamp: new Date(),
		});
	}

	/**
	 * Closes the modal.
	 */
	close(): void {
		this.ngbmodalService.dismiss();
	}
}
